import React from "react";
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { sharedApi } from "@/lib/api";
import { useMySubscription } from "@/hooks/useSubscription";
import Seo from "@/components/common/Seo";
import Breadcrumbs from "@/components/common/Breadcrumbs";
import { Loader2, Crown, Calendar, Zap, ArrowUpRight, Package } from "lucide-react";
import { toast } from "sonner";

/**
 * Gap 2 — Manage subscription page.
 * IMPORTANT: plans are one-time 30-day Stripe Checkout payments (NOT auto-recurring).
 * Nothing to cancel — a paid plan simply lapses back to Free at `ends_at`.
 * Do NOT show a cancel button.
 * Backend: GET /classifieds/subscriptions/me
 * Upgrade: POST /classifieds/subscriptions/checkout {plan_slug, origin_url}
 */
export default function MySubscriptionPage() {
  const q = useMySubscription();
  const sub = q.data?.subscription;
  const usage = q.data?.usage || {};

  const checkoutMut = useMutation({
    mutationFn: async (plan_slug) => {
      const { data } = await sharedApi.post("/classifieds/subscriptions/checkout", {
        plan_slug,
        origin_url: window.location.origin,
      });
      return data;
    },
    onSuccess: (data) => {
      if (data?.url) window.location.href = data.url;
      else toast.error("Couldn't start checkout — please try again.");
    },
    onError: (err) => toast.error(err?.response?.data?.detail || "Checkout failed"),
  });

  const planName = sub?.plan_name || sub?.plan_slug || "Free";
  const isPaid = sub?.plan_slug && sub.plan_slug !== "free";
  const endsAt = usage?.ends_at || sub?.ends_at;
  const endsAtLabel = formatDate(endsAt);

  return (
    <div className="max-w-4xl mx-auto px-4 md:px-8 py-8 md:py-12">
      <Seo title="My subscription" description="Manage your Allsale plan, view usage and upgrade any time." />
      <Breadcrumbs items={[{ label: "My subscription" }]} />

      <div className="mt-6 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-black text-slate-900">My subscription</h1>
          <p className="mt-2 text-slate-500">One-time payment per period — no auto-renew, no surprises.</p>
        </div>
        <Link
          to="/plan-and-pricing"
          data-testid="my-subscription-see-plans"
          className="inline-flex items-center gap-1.5 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white font-semibold text-sm px-4 py-2 transition-colors"
        >
          See all plans <ArrowUpRight className="w-4 h-4" />
        </Link>
      </div>

      {q.isLoading ? (
        <div className="mt-8 flex items-center justify-center py-16 text-slate-400">
          <Loader2 className="w-6 h-6 animate-spin" />
        </div>
      ) : q.isError ? (
        <div
          data-testid="my-subscription-error"
          className="mt-8 rounded-2xl bg-white shadow-card p-8 text-center"
        >
          <div className="w-12 h-12 mx-auto rounded-full bg-red-50 text-red-600 grid place-items-center">
            <Crown className="w-5 h-5" />
          </div>
          <h2 className="mt-4 font-display text-lg font-bold text-slate-900">
            Couldn&apos;t load your subscription
          </h2>
          <p className="mt-2 text-sm text-slate-500 max-w-sm mx-auto">
            The billing service is briefly unavailable. Try again in a moment.
          </p>
          <button
            type="button"
            onClick={() => q.refetch()}
            data-testid="my-subscription-retry"
            className="mt-5 inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-blue)] hover:bg-[color:var(--brand-blue-hover)] text-white text-sm font-semibold px-5 py-2 transition-colors"
          >
            Retry
          </button>
        </div>
      ) : (
        <div
          className="mt-8 rounded-2xl bg-gradient-to-br from-slate-900 via-slate-900 to-[#0F70A8] text-white p-6 md:p-8 shadow-card"
          data-testid="my-subscription-card"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-white/70">
                <Crown className="w-3.5 h-3.5" /> Current plan
              </div>
              <div className="mt-2 flex items-baseline gap-3">
                <span className="font-display text-4xl md:text-5xl font-black" data-testid="my-subscription-plan-name">
                  {planName}
                </span>
                <span
                  className={`inline-flex items-center gap-1 rounded-full text-xs font-bold px-2.5 py-1 ${
                    sub?.status === "active"
                      ? "bg-emerald-500/20 text-emerald-300"
                      : "bg-white/10 text-white/70"
                  }`}
                  data-testid="my-subscription-status"
                >
                  {sub?.status || "active"}
                </span>
              </div>
            </div>

            {isPaid && endsAtLabel && (
              <div className="text-right">
                <div className="text-[10px] font-bold uppercase tracking-widest text-white/60">Runs until</div>
                <div className="mt-1 inline-flex items-center gap-1.5 text-lg font-display font-bold" data-testid="my-subscription-ends-at">
                  <Calendar className="w-4 h-4 opacity-70" /> {endsAtLabel}
                </div>
              </div>
            )}
          </div>

          {isPaid && endsAtLabel && (
            <p className="mt-3 text-white/80 text-sm leading-relaxed" data-testid="my-subscription-renew-note">
              Your {planName} runs until {endsAtLabel}. It doesn&apos;t auto-renew — renew or upgrade any time.
            </p>
          )}
          {!isPaid && (
            <p className="mt-3 text-white/80 text-sm leading-relaxed" data-testid="my-subscription-free-note">
              You&apos;re on the Free plan. Upgrade for more active ads and boost credits.
            </p>
          )}

          {/* Usage stats */}
          <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-3">
            <StatTile
              icon={<Package className="w-4 h-4" />}
              label="Active ads"
              value={
                usage.listing_limit == null
                  ? `${usage.active_listings ?? 0} · Unlimited`
                  : `${usage.active_listings ?? 0} / ${usage.listing_limit}`
              }
              testid="my-subscription-listings"
            />
            <StatTile
              icon={<Zap className="w-4 h-4" />}
              label="Boost credits"
              value={
                usage.boosts_remaining != null
                  ? `${usage.boosts_remaining} left`
                  : "—"
              }
              testid="my-subscription-boosts"
            />
            {isPaid && endsAtLabel && (
              <StatTile
                icon={<Calendar className="w-4 h-4" />}
                label="Expires"
                value={endsAtLabel}
                testid="my-subscription-expires"
              />
            )}
          </div>

          {/* CTA row */}
          <div className="mt-6 flex flex-wrap gap-3">
            {isPaid ? (
              <button
                type="button"
                data-testid="my-subscription-renew"
                onClick={() => checkoutMut.mutate(sub.plan_slug)}
                disabled={checkoutMut.isPending}
                className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white text-sm font-bold px-5 py-2.5 transition-colors disabled:opacity-70"
              >
                {checkoutMut.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                Renew {planName}
              </button>
            ) : null}
            <Link
              to="/plan-and-pricing"
              data-testid="my-subscription-upgrade"
              className="inline-flex items-center gap-2 rounded-full bg-white hover:bg-white/90 text-slate-900 text-sm font-bold px-5 py-2.5 transition-colors"
            >
              {isPaid ? "Change plan" : "Upgrade"} <ArrowUpRight className="w-4 h-4" />
            </Link>
            <Link
              to="/my-ads"
              className="inline-flex items-center gap-2 rounded-full bg-white/10 hover:bg-white/15 text-white text-sm font-semibold px-5 py-2.5 transition-colors"
            >
              Manage my ads
            </Link>
          </div>
        </div>
      )}

      <p className="mt-8 text-xs text-slate-400 text-center">
        Need help with billing?{" "}
        <Link to="/contact-us" className="font-semibold text-[color:var(--brand-blue)] hover:underline">
          Talk to us
        </Link>
        .
      </p>
    </div>
  );
}

function StatTile({ icon, label, value, testid }) {
  return (
    <div className="rounded-xl bg-white/5 border border-white/10 px-4 py-3" data-testid={testid}>
      <div className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-white/60">
        {icon} {label}
      </div>
      <div className="mt-1 font-display text-lg font-bold text-white">{value}</div>
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-NZ", { day: "numeric", month: "long", year: "numeric" });
}
